import React from 'react';
import { FiArrowLeft, FiMail, FiPhone } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { usePageMeta } from '../utils/pageMeta.util';

const PrivacyNoticePage: React.FC = () => {
  usePageMeta({
    title: 'HIPAA Privacy Notice | Home Care Admin Experts',
    description:
      'HIPAA Privacy Notice describing how Protected Health Information may be used and disclosed by Home Care Admin Experts.',
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800 transition-colors mb-4 sm:mb-6"
        >
          <FiArrowLeft size={16} />
          Back
        </Link>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm px-4 py-6 sm:px-10 sm:py-10">
          <div className="mb-6">
            <p className="text-xl sm:text-3xl font-bold text-brand-700 mb-2 text-center">
              Home Care Admin Experts Privacy Policy
            </p>
            <h1 className="text-lg sm:text-2xl font-semibold text-slate-900 mb-1 text-left">HIPAA Privacy Notice</h1>
            <p className="text-xs sm:text-sm text-slate-500 text-left">Effective Date: January 1, 2025</p>
          </div>

          <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-8">
            This Notice Describes How Protected Health Information (PHI) May Be Used and Disclosed by Home
            Care Admin Experts, a division of Iron Heart Medical LLC, when providing services to home care
            agencies. Please review it carefully.
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                Who We Are
              </h2>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-3">
                Home Care Admin Experts (&ldquo;HCAE,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
                &ldquo;our&rdquo;) provides administrative and operational support services to licensed home
                care agencies. These services may include scheduling coordination, after-hours on-call
                support, recruiting assistance, and administrative management.
              </p>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-3">
                When performing these services, we may access certain information contained within the
                agency&rsquo;s software systems, electronic medical record systems, or scheduling platforms.
              </p>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                Home Care Admin Experts functions as a Business Associate to the home care agencies we
                support and complies with applicable provisions of the Health Insurance Portability and
                Accountability Act (HIPAA).
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                Information We May Access or Handle
              </h2>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-4">
                While performing contracted services, we may have access to limited information including:
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                  <p className="text-sm font-semibold text-slate-800 mb-2">Client Information</p>
                  <ul className="space-y-1.5">
                    {['Names', 'Contact information', 'Service schedules', 'Limited care coordination details necessary for scheduling or communication'].map(
                      (b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-700 flex-shrink-0" />
                          {b}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
                <div className="rounded-xl bg-slate-50 border border-slate-100 p-4">
                  <p className="text-sm font-semibold text-slate-800 mb-2">Caregiver Information</p>
                  <ul className="space-y-1.5">
                    {['Names', 'Contact information', 'Credentials and certifications', 'Work schedules and availability'].map(
                      (b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-700 flex-shrink-0" />
                          {b}
                        </li>
                      ),
                    )}
                  </ul>
                </div>
              </div>

              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                We do not own or control this information. All data remains the property and responsibility
                of the agency whose systems we access.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                How We Use and Disclose Information
              </h2>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-4">
                We may use or disclose Protected Health Information only as permitted or required under
                HIPAA, applicable law, and our Business Associate Agreements with the agencies we serve.
              </p>

              <p className="text-sm sm:text-base font-semibold text-slate-800 mb-2">Permitted Uses</p>

              <div className="space-y-4">
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1.5">Administrative Support</p>
                  <ul className="space-y-1.5">
                    {['Coordinating schedules and services', 'Communicating with caregivers and agency staff', 'Managing after-hours calls and care coordination'].map(
                      (b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-700 flex-shrink-0" />
                          {b}
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1.5">Recruitment Support</p>
                  <ul className="space-y-1.5">
                    {['Processing caregiver applications', 'Verifying credentials or documentation', 'Coordinating interviews and onboarding activities'].map(
                      (b) => (
                        <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-700 flex-shrink-0" />
                          {b}
                        </li>
                      ),
                    )}
                  </ul>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1.5">Legal and Regulatory Compliance</p>
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                    Responding to valid legal requests such as subpoenas, court orders, or government
                    investigations when required by law.
                  </p>
                </div>

                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-1.5">Operational Quality Improvement</p>
                  <ul className="space-y-1.5">
                    {['Internal training', 'Quality assurance reviews', 'Operational improvement processes'].map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-700 flex-shrink-0" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mt-4">
                Home Care Admin Experts does not use or disclose PHI for marketing or unrelated purposes
                without written authorization from the agency responsible for the information.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                Agency Responsibilities
              </h2>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-4">
                The home care agency that contracts with Home Care Admin Experts remains the Covered Entity
                under HIPAA and is responsible for:
              </p>
              <ul className="space-y-1.5 mb-4">
                {[
                  'Maintaining HIPAA-compliant policies and procedures',
                  'Informing their clients and caregivers about privacy rights',
                  'Controlling user access within their own systems',
                  'Ensuring appropriate data sharing permissions',
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm sm:text-base text-slate-700">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-700 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                Agencies may request reasonable limitations on how we access or use information within the
                scope of our services.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                Safeguarding Information
              </h2>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-4">
                Home Care Admin Experts implements appropriate administrative, technical, and physical
                safeguards designed to protect Protected Health Information from unauthorized access, use,
                or disclosure.
              </p>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-2">These safeguards include:</p>
              <ul className="space-y-1.5 mb-4">
                {[
                  'Staff confidentiality agreements',
                  'HIPAA training for employees and contractors',
                  'Secure system access practices',
                  'Controlled access to client platforms and scheduling systems',
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm sm:text-base text-slate-700">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-700 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                Access to agency systems is limited to personnel who require the information to perform
                contracted services.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                Complaints
              </h2>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-3">
                If an agency believes its privacy rights have been violated, a complaint may be submitted to
                Home Care Admin Experts or directly to the U.S. Department of Health and Human Services,
                Office for Civil Rights.
              </p>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                All complaints will be reviewed promptly and there will be no retaliation for filing a
                complaint.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                Changes to This Notice
              </h2>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-3">
                Home Care Admin Experts reserves the right to update this Privacy Notice at any time to
                reflect operational, legal, or regulatory changes.
              </p>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                Updated versions will apply to all information accessed in the course of providing services.
                The most current version of this notice will be available upon request.
              </p>
            </section>

            <section>
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-3 pb-2 border-b border-slate-100">
                Contact Information
              </h2>
              <p className="text-sm sm:text-base text-slate-700 leading-relaxed mb-4">
                If you have questions regarding this notice or our privacy practices, please contact our
                administrative office using the information below.
              </p>

              <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 sm:p-5 space-y-2.5">
                <p className="text-sm font-semibold text-slate-800">Home Care Admin Experts</p>
                <p className="text-sm text-slate-600">A Division of Iron Heart Medical LLC</p>
                <p className="text-sm text-slate-600">15985 Preserve Marketplace Blvd., Odessa, FL 33556</p>
                <a
                  href="tel:+17278103943"
                  className="flex items-center gap-2 text-sm text-brand-700 hover:text-brand-800 transition-colors w-fit"
                >
                  <FiPhone size={14} />
                  (727) 810-3943
                </a>
                <a
                  href="mailto:info@iheart-hc.com"
                  className="flex items-center gap-2 text-sm text-brand-700 hover:text-brand-800 transition-colors w-fit break-all"
                >
                  <FiMail size={14} />
                  info@iheart-hc.com
                </a>
              </div>
            </section>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          &copy; {new Date().getFullYear()} Home Care Admin Experts, a division of Iron Heart Medical LLC. All rights reserved.
        </p>
      </main>
    </div>
  );
};

export default PrivacyNoticePage;
